import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage, Language } from '../contexts/LanguageContext';
type QA = {
  question: string;
  answer: string;
};
type FaqStrings = {
  heading: string;
  subtitle: string;
  faqs: QA[];
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, FaqStrings> = {
  en: {
    heading: 'Frequently Asked Questions',
    subtitle: 'Clear answers to help you understand our service.',
    faqs: [{
      question: 'Are you affiliated with the U.S. government?',
      answer: 'No. Noor Passport Services is a private, non-government document preparation service. We help you fill out your forms accurately, but we do not process applications, issue passports, or interact with the Department of State on your behalf.'
    }, {
      question: 'Do I still need to pay government fees?',
      answer: "Yes. Our fee only covers the preparation service. You still pay the U.S. Department of State's own application and execution fees, per person, when you submit your forms in person."
    }, {
      question: 'What if I need more than one form?',
      answer: 'Our price is per person, not per form. If your case needs more than one form — for example, when a passport is lost and you must report it and apply for a new one at the same time — we help you with all the forms you need, at no extra cost. We only charge more for additional people.'
    }, {
      question: 'What if my documents are in another language?',
      answer: 'For some applications — especially for minors — the government requires that documents in another language (for example, a foreign birth certificate that proves your relationship to the child) come with a certified English translation. Noor does not translate documents, but we can connect you with a trusted certified translation service.'
    }, {
      question: 'Can I just do this myself for free?',
      answer: 'Absolutely. The blank forms are free on the U.S. Department of State website. Our service is for people who prefer a simpler, guided experience in their own language, so their forms are filled out correctly.'
    }, {
      question: 'What happens after I finish?',
      answer: 'After you complete your answers and pay our fee, we prepare your official form. With a “with help” package, a Noor specialist reviews it. You then review and approve it, and receive it by email (free) or printed and mailed — along with a checklist of the documents to bring to your appointment.'
    }, {
      question: 'Is my personal information secure?',
      answer: 'Yes. We take your privacy seriously, use encryption to protect your data, and never sell your personal information.'
    }]
  },
  es: {
    heading: 'Preguntas frecuentes',
    subtitle: 'Respuestas claras para entender nuestro servicio.',
    faqs: [{
      question: '¿Están afiliados al gobierno de EE. UU.?',
      answer: 'No. Noor Passport Services es un servicio privado de preparación de documentos, no gubernamental. Le ayudamos a llenar sus formularios correctamente, pero no tramitamos solicitudes, no emitimos pasaportes ni actuamos ante el Departamento de Estado en su nombre.'
    }, {
      question: '¿Tengo que pagar igual las tarifas del gobierno?',
      answer: 'Sí. Nuestra tarifa cubre solo el servicio de preparación. Usted paga aparte las tarifas de solicitud y de ejecución del Departamento de Estado de EE. UU., por persona, cuando entrega sus formularios en persona.'
    }, {
      question: '¿Y si necesito más de un formulario?',
      answer: 'Nuestro precio es por persona, no por formulario. Si su caso necesita más de un formulario — por ejemplo, cuando se pierde un pasaporte y hay que reportarlo y solicitar uno nuevo a la vez — le ayudamos con todos los formularios que hagan falta, sin costo adicional. Solo cobramos más cuando son personas adicionales.'
    }, {
      question: '¿Y si mis documentos están en otro idioma?',
      answer: 'Para algunos trámites — sobre todo de menores — el gobierno pide que los documentos en otro idioma (por ejemplo, un acta de nacimiento extranjera que prueba la relación con el niño) vengan con una traducción certificada al inglés. Noor no traduce documentos, pero lo conectamos con un servicio de traducción certificada de confianza.'
    }, {
      question: '¿Puedo hacerlo yo mismo gratis?',
      answer: 'Claro que sí. Los formularios en blanco son gratis en el sitio del Departamento de Estado de EE. UU. Nuestro servicio es para quienes prefieren una experiencia más sencilla y guiada en su propio idioma, para que sus formularios queden bien llenados.'
    }, {
      question: '¿Qué pasa después de que termino?',
      answer: 'Después de completar sus respuestas y pagar nuestra tarifa, preparamos su formulario oficial. Con un paquete “con ayuda”, un especialista de Noor lo revisa. Luego usted lo revisa y lo aprueba, y lo recibe por correo (gratis) o impreso y enviado — junto con una lista de los documentos que debe llevar a su cita.'
    }, {
      question: '¿Está segura mi información personal?',
      answer: 'Sí. Tomamos su privacidad en serio, usamos cifrado para proteger sus datos y nunca vendemos su información personal.'
    }]
  },
  ht: {
    heading: 'Kesyon yo poze souvan',
    subtitle: 'Repons klè pou ede ou konprann sèvis nou an.',
    faqs: [{
      question: 'Èske nou afilye ak gouvènman ameriken an?',
      answer: 'Non. Noor Passport Services se yon sèvis prive pou prepare dokiman, li pa gouvènmantal. Nou ede ou ranpli fòm ou yo kòrèkteman, men nou pa trete aplikasyon, nou pa bay paspò, ni nou pa aji devan Depatman Deta a nan non ou.'
    }, {
      question: 'Èske mwen toujou dwe peye frè gouvènman an?',
      answer: 'Wi. Frè nou an kouvri sèlman sèvis preparasyon an. Ou peye apa frè aplikasyon ak frè egzekisyon Depatman Deta Etazini, pa moun, lè ou soumèt fòm ou yo an pèsòn.'
    }, {
      question: 'E si mwen bezwen plis pase yon fòm?',
      answer: 'Pri nou an se pou chak moun, pa pou chak fòm. Si ka ou a bezwen plis pase yon fòm — pa egzanp, lè yon paspò pèdi epi ou dwe rapòte l epi mande yon nouvo an menm tan — nou ede ou ak tout fòm ou bezwen yo, san frè anplüs. Nou sèlman mande plis lè gen moun anplüs.'
    }, {
      question: 'E si dokiman mwen yo nan yon lòt lang?',
      answer: 'Pou kèk demand — sitou pou minè — gouvènman an mande pou dokiman ki nan yon lòt lang (pa egzanp, yon batistè etranje ki pwouve relasyon ou ak timoun nan) vini ak yon tradiksyon sètifye an anglè. Noor pa tradui dokiman, men nou ka konekte ou ak yon sèvis tradiksyon sètifye ou ka fè konfyans.'
    }, {
      question: 'Èske mwen ka fè li poukont mwen gratis?',
      answer: 'Wi, byensi. Fòm vid yo gratis sou sitwèb Depatman Deta Etazini. Sèvis nou an se pou moun ki pi renmen yon eksperyans pi senp e gide nan pwòp lang yo, pou fòm yo byen ranpli.'
    }, {
      question: 'Kisa ki rive apre mwen fini?',
      answer: 'Apre ou fin reponn kesyon yo epi peye frè nou an, nou prepare fòm ofisyèl ou an. Ak yon pakè “ak èd”, yon espesyalis Noor tcheke li. Apre sa ou revize li epi apwouve li, epi ou resevwa li pa imèl (gratis) oswa enprime epi voye — ansanm ak yon lis dokiman pou pote nan randevou ou.'
    }, {
      question: 'Èske enfòmasyon pèsonèl mwen an an sekirite?',
      answer: 'Wi. Nou pran konfidansyalite ou oseryez, nou itilize chifreman pou pwoteje done ou yo, epi nou pa janm vann enfòmasyon pèsonèl ou.'
    }]
  },
  vi: {
    heading: 'Câu hỏi thường gặp',
    subtitle: 'Giải đáp rõ ràng để giúp quý vị hiểu dịch vụ của chúng tôi.',
    faqs: [{
      question: 'Quý vị có liên kết với chính phủ Hoa Kỳ không?',
      answer: 'Không. Noor Passport Services là một dịch vụ chuẩn bị giấy tờ tư nhân, phi chính phủ. Chúng tôi giúp quý vị điền mẫu đơn chính xác, nhưng không xử lý đơn, không cấp hộ chiếu, và không thay mặt quý vị làm việc với Bộ Ngoại giao.'
    }, {
      question: 'Tôi vẫn phải trả phí chính phủ chứ?',
      answer: 'Có. Phí của chúng tôi chỉ bao gồm dịch vụ chuẩn bị. Quý vị vẫn trả riêng phí nộp đơn và phí thực hiện của Bộ Ngoại giao Hoa Kỳ, theo mỗi người, khi quý vị nộp mẫu đơn trực tiếp.'
    }, {
      question: 'Nếu tôi cần nhiều hơn một mẫu đơn thì sao?',
      answer: 'Giá của chúng tôi tính theo từng người, không theo từng mẫu đơn. Nếu trường hợp của quý vị cần nhiều hơn một mẫu đơn — ví dụ, khi hộ chiếu bị mất và quý vị phải báo mất đồng thời xin cấp mới — chúng tôi giúp quý vị với tất cả các mẫu đơn cần thiết, không tính thêm phí. Chúng tôi chỉ tính thêm khi có thêm người.'
    }, {
      question: 'Nếu giấy tờ của tôi bằng ngôn ngữ khác thì sao?',
      answer: 'Đối với một số hồ sơ — nhất là cho trẻ vị thành niên — chính phủ yêu cầu các giấy tờ bằng ngôn ngữ khác (ví dụ, giấy khai sinh nước ngoài chứng minh quan hệ của quý vị với trẻ) phải kèm theo bản dịch tiếng Anh có chứng nhận. Noor không dịch giấy tờ, nhưng chúng tôi có thể kết nối quý vị với một dịch vụ dịch thuật có chứng nhận đáng tin cậy.'
    }, {
      question: 'Tôi có thể tự làm miễn phí không?',
      answer: 'Hoàn toàn được. Các mẫu đơn trống miễn phí trên trang web của Bộ Ngoại giao Hoa Kỳ. Dịch vụ của chúng tôi dành cho những người thích trải nghiệm đơn giản hơn, có hướng dẫn bằng chính ngôn ngữ của mình, để mẫu đơn được điền đúng.'
    }, {
      question: 'Sau khi hoàn tất thì điều gì xảy ra?',
      answer: 'Sau khi quý vị hoàn thành câu trả lời và thanh toán phí, chúng tôi chuẩn bị mẫu đơn chính thức của quý vị. Với gói “có hỗ trợ”, một chuyên viên Noor xem lại. Sau đó quý vị kiểm tra và phê duyệt, rồi nhận qua email (miễn phí) hoặc in và gửi — kèm theo danh sách giấy tờ cần mang đến buổi hẹn.'
    }, {
      question: 'Thông tin cá nhân của tôi có an toàn không?',
      answer: 'Có. Chúng tôi coi trọng quyền riêng tư của quý vị, dùng mã hóa để bảo vệ dữ liệu, và không bao giờ bán thông tin cá nhân của quý vị.'
    }]
  }
};
export function FAQ() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-24 bg-brand-bg relative z-10" data-id="element-152" data-mp="108fze8a6372v">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-153" data-mp="3gtxlmd1akss2">
        <div className="text-center mb-16" data-id="element-154" data-mp="3kwlx6she4dsq">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-155" data-mp="3kxdgp4xpql2n">
            {t.heading}
          </h2>
          <p className="text-lg text-brand-textSecondary" data-id="element-156" data-mp="3db8axrt0c5r9">{t.subtitle}</p>
        </div>

        <div className="space-y-4" data-id="element-157" data-mp="38dgf4mp8rzhs">
          {t.faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return <div key={index} className="bg-white border border-brand-border rounded-2xl overflow-hidden transition-all duration-200" data-id="element-158" data-mp="3icfipqv5h4xx" data-mpi={index}>
                <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-inset" aria-expanded={isOpen} data-id="element-159" data-mp="2pniayhokeqjf">
                  <span className="text-lg font-semibold text-brand-textPrimary pr-8" data-id="element-160" data-mp="2bvk82olg5qvp">
                    {faq.question}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-brand-textSecondary flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-161" data-mp="26whatqbpkoya" />
                </button>

                <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-162" data-mp="30kdf6lztcbdy">
                  <p className="text-brand-textSecondary leading-relaxed" data-id="element-163" data-mp="3aenhavek4o1v">
                    {faq.answer}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}